import { Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { FormControl, Validators } from '@angular/forms';
import { AssessmentAnswerInterface, AssessmentQuestionInterface } from 'app/assessment/models';
import { RecommendationTemplateRuleInterface } from 'app/recommendation/models';
import { AssessmentQuestionDataInterface } from 'app/recommendation/models/assessment-question-data.model';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

import { DefaultLanguageStringsService } from '../../../core/language/services/default-language-strings.service';

@Component({
  selector: 'cz-recommendations-configuration-survey-rule',
  templateUrl: './recommendations-configuration-survey-rule.component.html',
  styleUrls: ['./recommendations-configuration-survey-rule.component.scss'],
})
export class RecommendationsConfigurationSurveyRuleComponent implements OnInit, OnDestroy {
  @Input() public rule: RecommendationTemplateRuleInterface;
  @Input() public ruleData: AssessmentQuestionDataInterface;

  @Output() public ruleSaved: EventEmitter<RecommendationTemplateRuleInterface>;
  @Output() public ruleDeleted: EventEmitter<RecommendationTemplateRuleInterface>;

  public questionControl: FormControl;
  public answerControl: FormControl;

  public editRule: boolean = false;
  public ruleNotActive: boolean = false;
  public newRule: boolean = false;
  public edited: boolean = false;

  public selectedQuestion: AssessmentQuestionInterface;

  private destroy$: Subject<boolean> = new Subject<boolean>();

  constructor(public readonly _defaultStrings: DefaultLanguageStringsService) {
    this.questionControl = new FormControl({ Validators: Validators.required, updateOn: 'blur', value: '' });
    this.answerControl = new FormControl({ Validators: Validators.required, updateOn: 'blur', value: '' });
    this.ruleSaved = new EventEmitter<RecommendationTemplateRuleInterface>();
    this.ruleDeleted = new EventEmitter<RecommendationTemplateRuleInterface>();
  }

  public ngOnInit():void {
    if(!this.rule) {
      this.rule = {
        id: undefined,
        questionGroup: undefined,
        questionId: undefined,
        answerId: undefined,
        recommendationId: undefined,
      };
      this.newRule = true;
      this.selectedQuestion = {
        answers: [],
      };
      this.answerControl.disable();
    } else {
      this.setQuestionAnswerFromRule();
    }

    this.questionControl.valueChanges
    .pipe(takeUntil(this.destroy$))
    .subscribe((question: AssessmentQuestionInterface) => {
      if(!question) { return; }

      if(this.selectedQuestion.id === question.id) { return; }

      this.selectedQuestion = question;
      this.answerControl.enable();
      this.answerControl.reset();
      this.edited = true;
    });

    this.answerControl.valueChanges
    .pipe(takeUntil(this.destroy$))
    .subscribe((answer: AssessmentAnswerInterface) => {
      if(answer.id === this.rule.answerId) { return; }

      this.edited = true;
    });
  }

  public ngOnDestroy(): void {
    this.destroy$.next(true);
    this.destroy$.complete();
  }

  public editingRule(): void {
    this.questionControl.enable();
    this.answerControl.enable();
    this.editRule = true;
  }

  public closeEditingRule(): void {
    if(this.edited) {
      this.setQuestionAnswerFromRule();
    }
    this.questionControl.disable();
    this.answerControl.disable();
    this.editRule = false;
    this.edited = false;
  }

  public saveRule(): void {
    this.editRule = false;
    if(!this.questionControl.valid || !this.answerControl.valid) { return; }

    const ruleQuestion: AssessmentQuestionInterface = this.questionControl.value;
    const ruleAnswer: AssessmentAnswerInterface = this.answerControl.value;

    const ruleData: RecommendationTemplateRuleInterface = {
      ...this.rule,
      questionGroup: ruleQuestion.group,
      questionId: ruleQuestion.id,
      answerId: ruleAnswer.id,
    };

    this.ruleSaved.emit(ruleData);

    if(this.newRule) {
      this.questionControl.reset();
      this.answerControl.reset();
      this.rule = {
        id: undefined,
        questionGroup: undefined,
        questionId: undefined,
        answerId: undefined,
        recommendationId: undefined,
      };
    } else {
      this.editRule = false;
    }
  }

  public deleteRule(): void {
    this.ruleDeleted.emit(this.rule);
  }

  private setQuestionAnswerFromRule(): void {
    const matchingQuestions: AssessmentQuestionInterface[] =
      this.ruleData.allQuestions.filter((question: AssessmentQuestionInterface) => {
        if(question.group === this.rule.questionGroup && question.id === this.rule.questionId) {
          return question;
        }
      });

    if(!matchingQuestions || matchingQuestions.length === 0) {
      this.ruleNotActive = true;
      return;
    }

    this.selectedQuestion = matchingQuestions[0];

    this.questionControl.patchValue(this.selectedQuestion);

    const matchingAnswers: AssessmentAnswerInterface[] =
      this.selectedQuestion.answers.filter((answer: AssessmentAnswerInterface) => {
        if(answer.id === this.rule.answerId) {
          return answer;
        }
      });

    if(!matchingAnswers || matchingAnswers.length === 0) {
      this.ruleNotActive = true;
      return;
    }

    this.answerControl.patchValue(matchingAnswers[0]);

    this.questionControl.disable();
    this.answerControl.disable();
  }
}
